import React, { useState, useEffect } from 'react'
import { toast } from '../toast/CustomToast'
import api from '../../utils/api'
import TraceTimeline from './TraceTimeline'
import PayloadViewer from './PayloadViewer'

// Complete class-name strings (not built via dynamic interpolation) so
// Tailwind's JIT scanner can find them as literal substrings of this file.
const METRIC_COLOR_THEMES = {
    blue: { icon: 'bg-blue-500', border: 'border-blue-500', cardBg: 'bg-blue-50 dark:bg-blue-950/20' },
    purple: { icon: 'bg-purple-500', border: 'border-purple-500', cardBg: 'bg-purple-50 dark:bg-purple-950/20' },
    emerald: { icon: 'bg-emerald-500', border: 'border-emerald-500', cardBg: 'bg-emerald-50 dark:bg-emerald-950/20' },
    orange: { icon: 'bg-orange-500', border: 'border-orange-500', cardBg: 'bg-orange-50 dark:bg-orange-950/20' },
}

const formatDuration = (ms) => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
}

// Auto-scales bytes into B / KB / MB / GB.
const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return null
    if (bytes < 1024) return `${Math.round(bytes)} B`
    const units = ['KB', 'MB', 'GB']
    let value = bytes / 1024
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex++
    }
    return `${value.toFixed(value < 10 ? 2 : value < 100 ? 1 : 0)} ${units[unitIndex]}`
}

const getCpuSeverity = (percent) => {
    if (percent === null || percent === undefined) return null
    if (percent < 30) return { label: 'Baixo' }
    if (percent < 70) return { label: 'Médio' }
    return { label: 'Alto' }
}

const MetricTile = ({ icon, color, label, value, sublabel }) => {
    const theme = METRIC_COLOR_THEMES[color]
    return (
        <div className={`flex items-stretch rounded-lg border overflow-hidden ${theme.border} ${theme.cardBg}`}>
            <div className={`flex items-center justify-center px-3 ${theme.icon}`}>
                <span className="material-icons text-white" style={{ fontSize: '20px' }}>{icon}</span>
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-center px-3 py-2">
                <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">{value ?? '-'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {label}{sublabel ? ` · ${sublabel}` : ''}
                </p>
            </div>
        </div>
    )
}

const TraceDetailModal = ({ trace, onClose, onReprocess }) => {
    const [loadingPayload, setLoadingPayload] = useState(false)
    const [payload, setPayload] = useState(null)
    const [showPayloadViewer, setShowPayloadViewer] = useState(false)
    const [reprocessing, setReprocessing] = useState(false)
    const [showMetrics, setShowMetrics] = useState(false)

    const loadPayload = async () => {
        if (!trace.payload) {
            toast.error('Este trace não possui payload armazenado')
            return
        }

        setLoadingPayload(true)
        try {
            const response = await api.get(`/api/event-traces/${trace.id}/payload`)
            setPayload(response.data)
            setShowPayloadViewer(true)
        } catch (error) {
            console.error('Error loading payload:', error)
            toast.error('Erro ao carregar payload')
        } finally {
            setLoadingPayload(false)
        }
    }

    const handleReprocess = async () => {
        if (!onReprocess) return

        setReprocessing(true)
        try {
            await onReprocess(trace)
        } catch (error) {
            console.error('Error reprocessing:', error)
        } finally {
            setReprocessing(false)
        }
    }

    const copyTraceId = () => {
        navigator.clipboard.writeText(trace.trace_id)
        toast.success('Trace ID copiado!')
    }

    const copyEventId = () => {
        navigator.clipboard.writeText(trace.cloud_event_id)
        toast.success('Event ID copiado!')
    }

    const formatDateTime = (dateStr) => {
        const d = new Date(dateStr)
        const pad = (n) => String(n).padStart(2, '0')
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }

    const hasFailed = trace.steps.some(s => s.status === 'failed')
    // trace.log_status is the correlated CloudEventLog row's status (backend-computed) -
    // keeps this consistent with the dashboard card even when step data is
    // missing/incomplete (see EventsTracePage.getTraceStatus for the same logic).
    const isRunning = trace.steps.some(s => s.status === 'running')
        || trace.log_status === 'processing' || trace.log_status === 'pending'

    const cpuSeverity = getCpuSeverity(trace.cpu_usage_percent)
    const hasResourceMetrics = trace.total_duration_ms != null || trace.cpu_usage_percent != null
        || trace.ram_usage_kb != null || trace.disk_usage_bytes != null

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
                onClick={onClose}
            />

            {/* Side Panel */}
            <div className="fixed right-0 top-0 h-screen w-[900px] bg-white dark:bg-gray-800 shadow-2xl z-40 flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between p-6 pb-4">
                        <div className="flex items-start gap-4 flex-1">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-lg ${
                                hasFailed
                                    ? 'bg-gradient-to-br from-red-500 to-orange-500'
                                    : isRunning
                                    ? 'bg-gradient-to-br from-blue-500 to-cyan-500'
                                    : 'bg-gradient-to-br from-green-500 to-emerald-500'
                            }`}>
                                <span className="material-icons text-white" style={{ fontSize: '28px' }}>
                                    {hasFailed ? 'error' : isRunning ? 'autorenew' : 'check_circle'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                    Detalhes do Trace
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 mb-1">Trace ID</p>
                                        <div className="flex items-center gap-2">
                                            <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono text-gray-900 dark:text-white">
                                                {trace.trace_id}
                                            </code>
                                            <button
                                                onClick={copyTraceId}
                                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                                            >
                                                <span className="material-icons" style={{ fontSize: '18px' }}>content_copy</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 mb-1">CloudEvent ID</p>
                                        <div className="flex items-center gap-2">
                                            <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono text-gray-900 dark:text-white truncate max-w-[200px]">
                                                {trace.cloud_event_id}
                                            </code>
                                            <button
                                                onClick={copyEventId}
                                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                                            >
                                                <span className="material-icons" style={{ fontSize: '18px' }}>content_copy</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 mb-1">Tipo de Evento</p>
                                        <span className="inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                                            {trace.event_type}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 mb-1">Source</p>
                                        <span className="text-gray-900 dark:text-white text-xs font-mono truncate block max-w-[200px]">
                                            {trace.source}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 dark:text-gray-400"
                        >
                            <span className="material-icons">close</span>
                        </button>
                    </div>

                    {/* Resource Metrics accordion - discreet, secondary info, collapsed by default */}
                    {hasResourceMetrics && (
                        <div className="px-6 pb-4">
                            <button
                                onClick={() => setShowMetrics(v => !v)}
                                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition"
                            >
                                <span className="material-icons" style={{ fontSize: '16px' }}>
                                    {showMetrics ? 'expand_less' : 'expand_more'}
                                </span>
                                Métricas de processamento
                            </button>

                            {showMetrics && (
                                <div className="mt-3">
                                    <p className="mb-2 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                                        <span className="material-icons" style={{ fontSize: '14px' }}>info</span>
                                        Dados coletados durante o processamento deste evento. São estimativas para monitoramento da plataforma SolidFlow.
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <MetricTile
                                            icon="timer"
                                            color="blue"
                                            label="Duração Total"
                                            value={formatDuration(trace.total_duration_ms)}
                                        />
                                        <MetricTile
                                            icon="speed"
                                            color="purple"
                                            label="CPU"
                                            value={trace.cpu_usage_percent != null
                                                ? `${trace.cpu_usage_percent.toFixed(trace.cpu_usage_percent < 10 ? 1 : 0)}%`
                                                : null}
                                            sublabel={cpuSeverity?.label}
                                        />
                                        <MetricTile
                                            icon="memory"
                                            color="emerald"
                                            label="Memória (RAM)"
                                            value={trace.ram_usage_kb != null ? formatBytes(trace.ram_usage_kb * 1024) : null}
                                        />
                                        <MetricTile
                                            icon="storage"
                                            color="orange"
                                            label="Disco"
                                            value={trace.disk_usage_bytes != null ? formatBytes(trace.disk_usage_bytes) : null}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions Bar */}
                <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        {trace.payload && (
                            <button
                                onClick={loadPayload}
                                disabled={loadingPayload}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition font-medium text-sm shadow-sm"
                            >
                                <span className="material-icons" style={{ fontSize: '18px' }}>
                                    {loadingPayload ? 'hourglass_empty' : 'code'}
                                </span>
                                {loadingPayload ? 'Carregando...' : 'Ver Payload'}
                            </button>
                        )}

                        {hasFailed && onReprocess && (
                            <button
                                onClick={handleReprocess}
                                disabled={reprocessing}
                                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg transition font-medium text-sm shadow-sm"
                            >
                                <span className="material-icons" style={{ fontSize: '18px' }}>
                                    {reprocessing ? 'hourglass_empty' : 'refresh'}
                                </span>
                                {reprocessing ? 'Reprocessando...' : 'Reprocessar'}
                            </button>
                        )}

                        <div className="flex-1"></div>

                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                            <span className="material-icons" style={{ fontSize: '14px' }}>schedule</span>
                            {formatDateTime(trace.created_at)}
                        </span>
                </div>

                {/* Content - Timeline */}
                <div className="flex-1 overflow-auto p-6">
                    <TraceTimeline steps={trace.steps} />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                        <span className="material-icons" style={{ fontSize: '14px' }}>list</span>
                        {trace.steps.length} step(s)
                    </span>
                </div>
            </div>

            {/* Payload Viewer Modal */}
            {showPayloadViewer && payload && (
                <PayloadViewer
                    payload={payload}
                    onClose={() => setShowPayloadViewer(false)}
                />
            )}
        </>
    )
}

export default TraceDetailModal
